"use client";

export type EntityAliasType = "token" | "account";

export interface EntityAliasRecord {
	type: EntityAliasType;
	alias: string;
	targetId: string;
}

export interface EntityAliasMatch extends EntityAliasRecord {
	normalizedAlias: string;
}

const BUILTIN_ENTITY_ALIASES: EntityAliasRecord[] = [
	{ type: "token", alias: "宝贝", targetId: "2rfi" },
	{ type: "token", alias: "Baby", targetId: "2rfi" },
	{ type: "token", alias: "baby", targetId: "2rfi" },
	{ type: "token", alias: "BabyOdin", targetId: "2rfi" },
	{ type: "token", alias: "babyodin", targetId: "2rfi" },
	{ type: "token", alias: "奥丁宝贝", targetId: "2rfi" },
	{ type: "token", alias: "宝贝奥丁", targetId: "2rfi" },
	{ type: "token", alias: "奥丁狗", targetId: "2jjj" },
	{ type: "token", alias: "天平", targetId: "2dyb" },
	{ type: "token", alias: "汉堡", targetId: "28k1" },
	{ type: "token", alias: "比特钻石", targetId: "2ktp" },
	{ type: "token", alias: "π", targetId: "2n8d" },
	{ type: "token", alias: "派", targetId: "2n8d" },
	{ type: "token", alias: "金派", targetId: "2n8d" },
	{ type: "token", alias: "金π", targetId: "2n8d" },
	{ type: "token", alias: "中本聪", targetId: "2k6z" },
	{ type: "token", alias: "比特猫", targetId: "2bt1" },
	{ type: "token", alias: "孔子", targetId: "29m8" },
	{
		type: "account",
		alias: "bob账号",
		targetId: "vv5jb-7sm7u-vn3nq-6nflf-dghis-fd7ji-cx764-xunni-zosog-eqvpw-oae",
	},
	{
		type: "account",
		alias: "Bob账号",
		targetId: "vv5jb-7sm7u-vn3nq-6nflf-dghis-fd7ji-cx764-xunni-zosog-eqvpw-oae",
	},
];

export const SEARCH_RECOMMENDED_KEYWORDS = [
	"宝贝",
	"Bob账号",
	"2rfi",
	"BabyOdin",
] as const;

export function normalizeEntityAliasInput(value: string) {
	return value
		.normalize("NFKC")
		.toLowerCase()
		.replace(/[$]/g, "")
		.replace(/[“”"'`‘’]/g, "")
		.replace(/[，。！？、：；,.!?;:/\\()[\]{}<>]/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function normalizeCompactAliasInput(value: string) {
	return normalizeEntityAliasInput(value).replace(/\s+/g, "");
}

const NORMALIZED_ENTITY_ALIASES = BUILTIN_ENTITY_ALIASES.map((item) => ({
	...item,
	normalizedAlias: normalizeEntityAliasInput(item.alias),
}));
const CONTAINED_ENTITY_ALIASES = [...NORMALIZED_ENTITY_ALIASES].sort(
	(a, b) =>
		normalizeCompactAliasInput(b.alias).length -
		normalizeCompactAliasInput(a.alias).length,
);

const EXACT_ENTITY_ALIAS_MAP = new Map(
	NORMALIZED_ENTITY_ALIASES.map((item) => [item.normalizedAlias, item] as const),
);

export function getBuiltinTokenSlangMap(): Record<string, string> {
	return Object.fromEntries(
		BUILTIN_ENTITY_ALIASES.filter((item) => item.type === "token").map((item) => [
			item.alias,
			item.targetId,
		]),
	);
}

export function resolveEntityAliasExact(query: string): EntityAliasMatch | null {
	const normalized = normalizeEntityAliasInput(query);
	if (!normalized) return null;

	const match = EXACT_ENTITY_ALIAS_MAP.get(normalized);
	return match ? { ...match } : null;
}

export function findContainedTokenAlias(message: string): EntityAliasMatch | null {
	return findContainedEntityAlias(message, ["token"]);
}

export function findContainedEntityAlias(
	message: string,
	allowedTypes?: EntityAliasType[],
): EntityAliasMatch | null {
	const compactMessage = normalizeCompactAliasInput(message);
	if (!compactMessage) return null;
	const allowedTypeSet = allowedTypes ? new Set(allowedTypes) : null;

	for (const item of CONTAINED_ENTITY_ALIASES) {
		if (allowedTypeSet && !allowedTypeSet.has(item.type)) continue;
		if (compactMessage.includes(normalizeCompactAliasInput(item.alias))) {
			return { ...item };
		}
	}

	return null;
}
